/* med-shelf.js — product carousels + recently viewed (2026-09-16)
 *
 * Two jobs:
 *   1. Turn any [data-med-shelf] into a horizontal carousel with arrows that
 *      appear only when the track actually overflows.
 *   2. Remember which products the visitor opened (localStorage) and fill the
 *      "Recently viewed" shelf from that list.
 *
 * Cards for recently viewed are rendered SERVER-SIDE via the Section Rendering
 * API so prices stay correct across markets/currencies. See med-recent-card.liquid.
 */
(function () {
  'use strict';

  var KEY = 'med:recent';
  var MAX_STORED = 12;
  var MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // prices go stale; 7 days is plenty
  // Bump when snippets/med-card.liquid markup changes, so stored cards re-render.
  // 2 = sale pill replaced by the NOW/OFF disc (2026-09-28).
  var CARD_VERSION = 2;

  /* ---------------------------------------------------------------- storage
   * Each entry: { h: handle, html: rendered card, cur: currency code, lang: <html lang>, ts: ms, v: CARD_VERSION }
   * The card HTML is in the page's language, so entries only replay on pages of the same language.
   * We keep the Liquid-rendered HTML rather than re-fetching, so money stays
   * formatted by the server in the visitor's own market currency.
   */

  function readList() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      var now = Date.now();
      return list.filter(function (e) {
        return e && typeof e.h === 'string' && typeof e.html === 'string' &&
               e.html && e.v === CARD_VERSION && (now - (e.ts || 0)) < MAX_AGE_MS;
      });
    } catch (e) {
      return []; // private mode, blocked storage, corrupt JSON
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX_STORED)));
    } catch (e) {
      // Quota exceeded (cards are ~1.5KB each). Drop to the 4 newest and retry
      // once; if that still fails, recently viewed simply won't persist.
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 4))); } catch (e2) {}
    }
  }

  function currentHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function currentCurrency() {
    var tpl = document.getElementById('med-card-tpl');
    return tpl ? (tpl.dataset.currency || '') : '';
  }

  function pageLang() { return document.documentElement.lang || ''; }

  function record() {
    var tpl = document.getElementById('med-card-tpl');
    if (!tpl) return; // not a product page
    var handle = tpl.dataset.handle || currentHandle();
    var html = (tpl.innerHTML || '').trim();
    if (!handle || !html) return;

    var list = readList().filter(function (e) { return e.h !== handle; });
    list.unshift({ h: handle, html: html, cur: tpl.dataset.currency || '', lang: pageLang(), ts: Date.now(), v: CARD_VERSION });
    writeList(list);
  }

  /* ---------------------------------------------------------------- carousel */

  function step(track) {
    var card = track.querySelector('.m-prod');
    if (!card) return track.clientWidth;
    var styles = window.getComputedStyle(track);
    var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    var cardW = card.getBoundingClientRect().width + gap;
    // How many whole cards fit? The last visible card needs no trailing gap,
    // hence the + gap — without it an exact 4-up row measures as 3 and the
    // carousel pages by 3, leaving a ragged offset.
    var perView = Math.max(1, Math.floor((track.clientWidth + gap) / cardW));
    return cardW * perView;
  }

  function syncArrows(shelf) {
    var track = shelf.querySelector('[data-med-track]');
    var prev = shelf.querySelector('.m-shelf-nav--prev');
    var next = shelf.querySelector('.m-shelf-nav--next');
    if (!track || !prev || !next) return;
    // 2px tolerance: sub-pixel layout can leave scrollWidth a hair over clientWidth.
    var overflows = track.scrollWidth - track.clientWidth > 2;
    shelf.classList.toggle('is-scrollable', overflows);
    if (!overflows) {
      prev.hidden = true;
      next.hidden = true;
      return;
    }
    prev.hidden = false;
    next.hidden = false;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
  }

  function initShelf(shelf) {
    if (shelf.dataset.medShelfReady === '1') return;
    var track = shelf.querySelector('[data-med-track]');
    if (!track) return;
    shelf.dataset.medShelfReady = '1';

    var prev = shelf.querySelector('.m-shelf-nav--prev');
    var next = shelf.querySelector('.m-shelf-nav--next');

    // Paging by arrow. Two things to work around:
    //  1. scroll-snap-type:x mandatory re-snaps on every frame of a smooth
    //     scroll, so scrollBy lands short and erratically. Suspend snapping for
    //     the duration of the animation, then restore it — the target is itself
    //     a snap point, so restoring causes no visible jump.
    //  2. The scroll listener is rAF-gated and rAF freezes in hidden tabs, which
    //     would leave "prev" stuck disabled and swallow the next click. So sync
    //     arrow state on a timer too, not only from the scroll event.
    var snapTimer = null;
    function page(dir) {
      var max = track.scrollWidth - track.clientWidth;
      var target = Math.max(0, Math.min(max, track.scrollLeft + dir * step(track)));
      track.style.scrollSnapType = 'none';
      track.scrollTo({ left: target, behavior: 'smooth' });
      clearTimeout(snapTimer);
      snapTimer = setTimeout(function () {
        track.style.scrollSnapType = '';
        syncArrows(shelf);
      }, 520);
    }
    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });

    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; syncArrows(shelf); });
    }, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () { syncArrows(shelf); }).observe(track);
    } else {
      window.addEventListener('resize', function () { syncArrows(shelf); });
    }

    syncArrows(shelf);
    // Images load after first paint and change scrollWidth.
    window.addEventListener('load', function () { syncArrows(shelf); });
  }

  function initAllShelves(root) {
    (root || document).querySelectorAll('[data-med-shelf]').forEach(initShelf);
  }

  /* --------------------------------------------------------- recently viewed */

  function fillRecent() {
    var sections = document.querySelectorAll('[data-med-recent]');
    if (!sections.length) return;

    var here = currentHandle();
    var cur = currentCurrency();

    sections.forEach(function (section) {
      var track = section.querySelector('[data-med-recent-track]');
      if (!track) return;
      var limit = parseInt(track.dataset.medRecentLimit, 10) || 8;

      var entries = readList().filter(function (e) {
        if (e.h === here) return false;               // not the page you're on
        if (cur && e.cur && e.cur !== cur) return false; // currency switched
        if ((e.lang || '') !== pageLang()) return false;   // card text is in another language
        return true;
      }).slice(0, limit);

      if (!entries.length) return;

      // Build off-document, then attach once — one reflow, not one per card.
      var frag = document.createDocumentFragment();
      entries.forEach(function (e) {
        var holder = document.createElement('div');
        holder.innerHTML = e.html;
        var card = holder.querySelector('.m-prod');
        if (card) frag.appendChild(card);
      });
      if (!frag.childNodes.length) return;

      track.innerHTML = '';
      track.appendChild(frag);
      section.hidden = false;

      var shelf = section.querySelector('[data-med-shelf]');
      if (shelf) { initShelf(shelf); syncArrows(shelf); }
    });
  }

  /* ------------------------------------------------------- you may also like */

  // sections/med-recommendations.liquid renders empty on the product page;
  // Shopify only fills `recommendations` when the section is fetched from
  // /recommendations/products. Swap the rendered cards in, then wire the arrows.
  function fillRecs(root) {
    (root || document).querySelectorAll('[data-med-recs]').forEach(function (section) {
      if (section.dataset.medRecsDone === '1' || !section.dataset.url) return;
      section.dataset.medRecsDone = '1';
      fetch(section.dataset.url)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (text) {
          if (!text) return;
          var holder = document.createElement('div');
          holder.innerHTML = text;
          var fresh = holder.querySelector('[data-med-recs]');
          if (!fresh || !fresh.querySelector('.m-prod')) return; // nothing to suggest: stay hidden
          section.innerHTML = fresh.innerHTML;
          section.hidden = false;
          initAllShelves(section);
          var shelf = section.querySelector('[data-med-shelf]');
          if (shelf) syncArrows(shelf);
        })
        .catch(function () {});
    });
  }

  /* -------------------------------------------------------------------- boot */

  function boot() {
    record();       // must run before fillRecent so the current page is stored
    initAllShelves();
    fillRecent();
    fillRecs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders a section: re-init whatever came back.
  document.addEventListener('shopify:section:load', function (e) {
    initAllShelves(e.target);
    fillRecent();
    fillRecs(e.target);
  });
})();

/* Instagram cards: play an uploaded reel muted while hovered (desktop). */
(function () {
  if (!window.matchMedia || !window.matchMedia('(hover:hover)').matches) return;
  document.addEventListener('mouseover', function (e) {
    var card = e.target.closest && e.target.closest('.m-ig-card');
    var v = card && card.querySelector('video');
    if (v && v.paused) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
  });
  document.addEventListener('mouseout', function (e) {
    var card = e.target.closest && e.target.closest('.m-ig-card');
    if (!card || card.contains(e.relatedTarget)) return;
    var v = card.querySelector('video');
    if (v) v.pause();
  });
})();
